import {
  canReadConfiguration,
  EVENT_DATE_KEY,
  ORGA_WEEK_DATE_KEY,
  REGISTRATION_FORM_KEY,
  USEFUL_LINKS_KEY,
  LAST_EDITION_FESTIVAL_EVENT_STATS_KEY,
  type Configuration,
  type FestivalEventStatsConfigValue,
  type RegistrationFormConfigValue,
  type UsefulLinksConfigValue,
} from "@overbookd/configuration";
import { updateItemToList } from "@overbookd/list";
import {
  defaultVolunteerCommitmentPresentation,
  defaultStaffCommitmentPresentation,
} from "@overbookd/registration";
import { Duration, OverDate } from "@overbookd/time";
import { ConfigurationRepository } from "~/repositories/configuration.repository";
import { isHttpError } from "~/utils/http/http-error.utils";

type State = {
  configurations: Configuration[];
};

export const useConfigurationStore = defineStore("configuration", {
  state: (): State => ({
    configurations: [],
  }),
  getters: {
    get: (state) => (key: string) => {
      return state.configurations.find((c) => c.key === key)?.value;
    },
    eventStartDate(): Date {
      const eventDate = this.get(EVENT_DATE_KEY);
      const now = OverDate.now().date;
      if (!isObject(eventDate) || !("start" in eventDate)) return now;
      const start = eventDate.start;
      if (typeof start !== "string") return now;
      return OverDate.fromLocal(new Date(start)).date;
    },

    mondayBeforeEventDate(): Date {
      const duration = Duration.ONE_DAY.times(4);
      return OverDate.from(this.eventStartDate).minus(duration).date;
    },

    orgaWeekStartDate(): Date | null {
      const orgaWeekDate = this.get(ORGA_WEEK_DATE_KEY);
      if (!isObject(orgaWeekDate) || !("start" in orgaWeekDate)) return null;
      const start = orgaWeekDate.start;
      if (typeof start !== "string") return null;
      return OverDate.fromLocal(new Date(start)).date;
    },

    registrationForm(): RegistrationFormConfigValue {
      const registrationForm = this.get(REGISTRATION_FORM_KEY);
      const defaultValue: RegistrationFormConfigValue = {
        isVolunteerRegistrationOpen: true,
        volunteerDescription: defaultVolunteerCommitmentPresentation,
        staffDescription: defaultStaffCommitmentPresentation,
      };
      if (!isObject(registrationForm)) return defaultValue;

      const {
        isVolunteerRegistrationOpen,
        volunteerDescription,
        staffDescription,
      } = registrationForm;
      return {
        isVolunteerRegistrationOpen:
          typeof isVolunteerRegistrationOpen !== "boolean"
            ? true
            : isVolunteerRegistrationOpen,
        volunteerDescription:
          typeof volunteerDescription !== "string"
            ? defaultVolunteerCommitmentPresentation
            : volunteerDescription,
        staffDescription:
          typeof staffDescription !== "string"
            ? defaultStaffCommitmentPresentation
            : staffDescription,
      };
    },

    usefulLinks(): UsefulLinksConfigValue {
      const links = this.get(USEFUL_LINKS_KEY);
      if (!isObject(links)) return {};
      return {
        googleCalendar: toOptionalString(links.googleCalendar),
        slack: toOptionalString(links.slack),
        helpline: toOptionalString(links.helpline),
      };
    },

    lastEditionFestivalEventStats(): FestivalEventStatsConfigValue {
      const stats = this.get(LAST_EDITION_FESTIVAL_EVENT_STATS_KEY);
      if (!isObject(stats)) return { activities: [], tasks: [] };
      const { activities, tasks } = stats;
      return {
        activities: Array.isArray(activities) ? activities : [],
        tasks: Array.isArray(tasks) ? tasks : [],
      };
    },
  },
  actions: {
    async fetchAll() {
      const res = await ConfigurationRepository.getAll();
      if (isHttpError(res)) return;
      this.configurations = res;
    },

    async fetch(key: string) {
      const shouldBeAuthenticated = !canReadConfiguration(key, []);
      const res = shouldBeAuthenticated
        ? await ConfigurationRepository.fetch(key)
        : await ConfigurationRepository.fetchAsUnauthenticated(key);
      if (isHttpError(res)) return;
      this._updateConfig(res);
    },

    async save(config: Configuration) {
      const res = await ConfigurationRepository.save(config);
      if (isHttpError(res)) return;
      sendSuccessNotification("La configuration a été sauvegardée");
      this._updateConfig(res);
    },

    async _updateConfig(configuration: Configuration) {
      const index = this.configurations.findIndex(
        (c) => c.key === configuration.key,
      );
      const configurations =
        index !== -1
          ? updateItemToList(this.configurations, index, configuration)
          : [...this.configurations, configuration];
      this.configurations = configurations;
    },
  },
});

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && value !== undefined && typeof value === "object";
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}
